import { ApplicationRef, Component, ViewChild, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { Competency } from 'src/modules/enaio-certificates/shared/competency.model';
import { Pupil } from 'src/modules/enaio-certificates/shared/pupil.model';
import { SchoolClass } from 'src/modules/enaio-certificates/shared/school-class.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from "src/modules/utils/shared/dates";
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject, OrdinaryObjectNumber } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { CertificateGrade } from '../../shared/certificate-grade.entity';
import { Certificate } from '../../shared/certificate.entity';
import { ConductGrade } from '../../shared/conduct-grade.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { ExamGrade } from '../../shared/exam-grade.entity';
import { ExamType } from '../../shared/exam-type.entity';
import { Exam } from '../../shared/exam.entity';
import { GlobalSettings } from '../../shared/global-settings.entity';
import { GradeBookSchoolClass } from '../../shared/grade-book-school-class.entity';
import { GradeHistory } from '../../shared/grade-history.entity';
import { GradeSettings } from '../../shared/grade-settings.entity';
import { GradeType } from '../../shared/grade-type.enum';
import { NumberFormatMode } from '../../shared/number-format-mode.enum';
import { SchoolTypeSettings } from '../../shared/school-type-settings.entity';
import { GradeEditorComponent } from '../grade-editor/grade-editor.component';
import { GradeBookSchoolClassUpsertDto } from '../../shared/grade-book-school-class-upsert.dto';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';
import { ExamGradeUpsertDto } from '../../shared/exam-grade-upsert.dto';

@Component({
    selector: 'app-class-grades',
    templateUrl: './class-grades.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassGradesComponent extends ComponentView {

    _ExamType = ExamType;
    _GradeType = GradeType;
    _NumberFormatMode = NumberFormatMode;

    service = inject(EnaioCertificateService);
    applicationRef = inject(ApplicationRef);

    neededParams: ObjectOfSimpleDataTypes = { schoolClassId: "number", isHalfYear: "bool" };
    schoolClassId: number;
    isHalfYear: boolean;

    useHeadNotes = false; //Aktuell deaktiviert, da nicht benötigt

    editingAllowed = true;
    isPast = false;
    pupils: Pupil[] = [];
    pupilNamesById: OrdinaryObjectNumber<string>;
    competencies: Competency[] = [];
    domains: SelectItem[] = [];
    enaioSchoolClass: SchoolClass;
    schoolClass: GradeBookSchoolClass;
    oldSchoolClass: GradeBookSchoolClass;
    examsByDomain: OrdinaryObject<Exam[]> = {};
    selectedDomain: string;
    editedExam: Exam;
    globalSettings: GlobalSettings;
    settings: SchoolTypeSettings;
    gradeSettings: GradeSettings;
    conductCompetencies: Competency[] = [];
    averageMap: OrdinaryObject<number>;
    averageMapTextualGrade: OrdinaryObject<number>;
    conductGradeMap: OrdinaryObject<ConductGrade>;
    examTypeFactorsTable: TableData;
    overviewTable: TableData;
    historyTable: TableData;
    usesGradeCertificates = false;
    certificates: Certificate[];
    pupilsWithCertificates: number[] = [];
    certificateGrades: CertificateGrade[];
    certificateGradeMap: OrdinaryObject<string>;
    oldCertificateGradeMap: OrdinaryObject<string>;
    showTranslation = false;
    bufferedIsChanged = false;
    certificateGradeItems: FrontendFieldListItem[];
    pupilsTable: TableData;
    @ViewChild("opExamTypeFactors") opExamTypeFactors: any;
    supportsSemester = false;
    selectedSemester = 2;
    semesterOptions: FrontendFieldListItem[];

    un = "";
    async initParams(): Promise<boolean> {
        this.un = this.app.auth.getUserName();

        this.enaioSchoolClass = await this.service.restGetSchoolClass(this.schoolClassId);
        this.isPast = this.enaioSchoolClass.getYearNumber() < EnaioCertificatesTools.getCurrentYear();
        this.bufferedIsChanged = false;
        this.certificateGradeItems = FrontendFieldListItem.make(["", "1", "2", "3", "4", "5", "6", "erteilt", "nicht beurteilbar"], null, false);

        [this.pupils, this.competencies, this.schoolClass, this.globalSettings] = await Promise.all([
            this.service.restGetPupils(this.schoolClassId),
            RestEndpoint.main().find({ schoolClassId: this.schoolClassId }).run("api/cer/competency").list(Competency),
            RestEndpoint.main().find({ schoolClassId: this.schoolClassId, isHalfYear: this.isHalfYear }).run("api/cer/gradebookschoolclass").get(GradeBookSchoolClass),
            RestEndpoint.main().find({}).run("api/cer/globalsettings").get(GlobalSettings)
        ]);

        if (this.isPast) {
            this.pupils = [];
            for (let pupilId of Arrays.getUnique(Arrays.explode(this.schoolClass.exams, e => e.grades).map(g => g.pupilId))) {
                this.pupils = [...this.pupils, await this.service.restGetPupil(pupilId)];
            }
            this.pupils = Arrays.withoutNull(this.pupils);
        }

        this.app.updateNavigation(null, [
            { label: "Notenbuch", routerLink: ["/enaio-certificates", "grade-book", "home"] },
            { label: "Klasse " + this.enaioSchoolClass.getDisplayName(), routerLink: ["/enaio-certificates", "grade-book", "class", this.enaioSchoolClass.id, this.isHalfYear ? 1 : 0] }
        ]);
        this.settings = await RestEndpoint.main().find({schoolType: this.enaioSchoolClass.getSchoolType(), year: this.enaioSchoolClass.getYearNumber(), isHalfYear: this.isHalfYear}).
            run("api/cer/schooltypesettings").get(SchoolTypeSettings);
        this.gradeSettings = this.settings.getGradeSettings(this.enaioSchoolClass.grade);

        this.editingAllowed = this.globalSettings.editingAllowed;
        this.supportsSemester = this.globalSettings.getSemesterNumber(this.enaioSchoolClass.grade) == 2;
        if (!this.supportsSemester) {
            this.selectedSemester = null;
        }
        this.semesterOptions = [new FrontendFieldListItem(1, "1. Semester"), new FrontendFieldListItem(2, "2. Semester")];

        this.conductCompetencies = this.competencies.filter(c => c.isConductGrade());
        this.domains = Arrays.sort(Arrays.getUnique(this.competencies.filter(c => !c.isConductGrade()).map(c => c.domain))).map(c => ({label: c, value: c}));
        if (this.selectedDomain == null) {
            this.selectedDomain = this.domains[0].value;
        }

        this.usesGradeCertificates = this.enaioSchoolClass.usesGradeCertificates();
        if (this.usesGradeCertificates) {
            this.certificateGrades = await RestEndpoint.main().query({schoolClassId: this.schoolClassId}).run("api/cer/certificate/certificateGrades").list(CertificateGrade);
            this.certificateGradeMap = Arrays.toMap(this.certificateGrades, cg => cg.pupilId + '|' + cg.domain, cg => cg.grade);
            this.certificates = await RestEndpoint.main().find({ schoolClassId: this.schoolClassId, onlyStatus: true }).run("api/cer/certificate").list(Certificate);
            this.pupilsWithCertificates = this.certificates.map(item => item.pupilId);
        }

        this.pupilNamesById = {};
        for (let pupil of this.pupils) {
            this.pupilNamesById[pupil.id] = pupil.getFullName();
            if (this.usesGradeCertificates) {
                for (let domain of this.domains) {
                    let key = pupil.id + '|' + domain.value;
                    if (!(key in this.certificateGradeMap)) {
                        this.certificateGradeMap[key] = "";
                    }
                }
            }
        }
        this.oldCertificateGradeMap = Objects.cloneDeep(this.certificateGradeMap);

        this.schoolClass.fillMissingGrades(this.pupils, this.conductCompetencies);
        this.conductGradeMap = this.schoolClass.getConductGradeMap();
        this.examsUpdated();

        this.overviewTable = new TableData([
            new TableColumn("index", "#", TableCellType.int, 2, { 
                frozen: "left" 
            }),
            new TableColumn("lastName", "Nachname", TableCellType.string, 20, { 
                frozen: "left" 
            }),
            new TableColumn("firstName", "Vorname", TableCellType.string, 20, { 
                frozen: "left" 
            }),
            ...this.domains.map(domain => new TableColumn("domain_" + domain.value, domain.label, TableCellType.int, 5, {
                rotate90: true                
            })),
            new TableColumn("average", "Ø", TableCellType.string, 5, { 
                frozen: "right", 
                styleClass: "primary-color"
            }),
            this.gradeSettings.gradeType == GradeType.percent ? new TableColumn("averagePercent", "", TableCellType.string, 15, { 
                frozen: "right", 
                styleClass: "primary-color" 
            }) : null,
            this.gradeSettings.gradeType == GradeType.percent && this.gradeSettings.isUseTextualGrades() ? new TableColumn("averageText", "", TableCellType.string, 5, { 
                frozen: "right", 
                styleClass: "primary-color" 
            }) : null
        ], Arrays.mapWithIndex(this.pupils, (p, index) => new TableRow(Objects.toString(p.id), p, {
            index: index + 1,
            lastName: p.lastName,
            firstName: p.firstName,
            ...Arrays.toOrdinaryObject(this.domains, domain => "domain_" + domain.value, domain => this.formatValueBoth(this.averageMap[p.id + "|" + domain.label], this.averageMapTextualGrade[p.id + "|" + domain.label], this.gradeSettings.gradeType == GradeType.percent ? NumberFormatMode.decimalZero : NumberFormatMode.decimalOne)),
            average: this.formatValue(this.averageMap[p.id + "|"], NumberFormatMode.decimalOne),
            averagePercent: this.formatTextualGrade(this.averageMap[p.id + "|"]),
            averageText: this.formatValue(this.averageMapTextualGrade[p.id + "|"])
        })), ["index"]);

        this.historyTable = new TableData([
                new TableColumn("changeDate", "Datum/Uhrzeit", TableCellType.dateTime, 1),
                new TableColumn("changedBy", "Benutzer", TableCellType.string, 15),
                new TableColumn("pupilId", "Schüler", TableCellType.string, 20),
                new TableColumn("modification", "Änderung", TableCellType.string, -1)
        ], this.schoolClass.history.map(h => new TableRow(Objects.toString(h.id), h, {
            changeDate: h.changeDate,
            changedBy: h.changedBy,
            pupilId: this.pupilNamesById[h.pupilId] || "-Unbekannt-",
            modification: h.modification
        })), [new TableSortColumn("changeDate", false)]);
        this.oldSchoolClass = Objects.cloneDeep(this.schoolClass);

        this.updatePupilsTable();

        if (Utils.isNoe(this.timers)) {
            this.addTimer(new SmTimer("ClassGrades", 1000, 1000, () => {
                this.isChanged();
            }));
        }

        return true;
    }

    updatePupilsTable(): void {
        let exams = Arrays.sortBy(this.schoolClass.getExamsForSemester(this.globalSettings, this.enaioSchoolClass.grade, this.selectedSemester).filter(exam => exam.domain == this.selectedDomain), e => e.date, false);
       //  (this.examsByDomain[this.selectedDomain] ?? []).filter();
        this.pupilsTable = new TableData([
            new TableColumn("index", "#", TableCellType.int, 2, {
                frozen: "left"
            }),
            new TableColumn("lastName", "Nachname", TableCellType.string, 20, {
                frozen: "left"
            }),
            new TableColumn("firstName", "Vorname", TableCellType.string, 20, {
                frozen: "left"
            }),
            ...exams.map(exam => 
                new TableColumn("exam_" + exam.id, (exam.examType != null ? exam.examType.title : "") + "<br>" + exam.title + "<br>" + Dates.formatDefaultDate(exam.date) + "<br><br>Ø " + Utils.formatNumber(exam.average, 2), TableCellType.string, 30, { 
                    useHeaderWidthAsMin: false, 
                    keepHtmlHeader: true, 
                    editable: true, 
                    headerStyleClass: "mouse-pointer",
                    editor: new FrontendField("field", "Feld", FrontendFieldType.custom, { componentType: GradeEditorComponent }),
                                //TODO Wenn "" anstatt "&nbsp;" benutzt wird, bekommt Zelle beim Startes des Edits keinen Fokus
                                valueFormatter: e => {
                        let grade = e.value as ExamGrade;
                        return grade != null && grade.grade != -1 ? Objects.toString(grade.grade) + (this.showTranslation ? " " + this.formatTextualGrade(grade.grade, false, exam.isDictation()) : "") : "&nbsp;";
                    },
                    cellRenderer: params => {
                        let grade = params.value as ExamGrade;
                        if (grade?.hasDifferingTitle()) {
                            return "<span>" + params.valueFormatted + "<span class=\"p-badge ml-3\">!</span></span>";
                        }
                        else {
                            return params.valueFormatted;
                        } 
                    },
                    toolTipGetter: event => {
                        let grade = event.value as ExamGrade;
                        return grade?.hasDifferingTitle() ? Arrays.itemsToString(Arrays.withoutNull([Utils.isNoe(grade.title) ? null : "Thema: " + grade.title, grade.date == null ? null : "Datum: " + Dates.formatDefaultDate(grade.date)])) : null;
                    },
                    headerClicked: async (columnId) => {
                        await this.editExam(null, exams.find(exam2 => exam2.id == Utils.toNumber(Strings.removePrefix(columnId, "exam_"))));
                        return true;
                    }   
                })),
            new TableColumn("average", "Ø", TableCellType.string, 7, {
                frozen: "right"
            }),
            this.gradeSettings.gradeType == GradeType.percent ? new TableColumn("percent", "", TableCellType.string, 7, {
                frozen: "right"
            }) : null,
            this.gradeSettings.gradeType == GradeType.percent && this.gradeSettings.isUseTextualGrades() ? new TableColumn("textualGrade", "", TableCellType.string, 20, {
                frozen: "right"
            }) : null,
            this.usesGradeCertificates ? new TableColumn("certificateGrade", "Zeugnisnote", TableCellType.string, 15, {
                frozen: "right",
                editable: true,
                editableCallback: event => this.usesGradeCertificates && this.pupilsWithCertificates.includes(event.row.raw.id as number),
                editor: new FrontendField("field", "Feld", FrontendFieldType.dropdown, {
                    listItems: this.certificateGradeItems
                })
            }) : null

        ], Arrays.mapWithIndex(this.pupils, (p, index) => new TableRow(Objects.toString(p.id), p, {
            index: index + 1,
            firstName: p.firstName,
            lastName: p.lastName,
            ...Arrays.toOrdinaryObject(exams, exam => "exam_" + exam.id, exam => exam.getGradeForPupil(p.id)),
            average: new TableCell(this.formatValue(this.averageMap[p.id + '|' + this.selectedDomain]), { styleClass: "primary-color"}),
            percent: new TableCell(this.formatTextualGrade(this.averageMap[p.id + "|" + this.selectedDomain]), { styleClass: "primary-color"}),
            textualGrade: new TableCell(this.formatValue(this.averageMapTextualGrade[p.id + "|" + this.selectedDomain]), { styleClass: "primary-color"}),
            certificateGrade: this.usesGradeCertificates ? this.pupilsWithCertificates.includes(p.id) ? this.certificateGradeMap[p.id + '|' + this.selectedDomain] : new TableCell("Kein Zeugnis!", { styleClass: "xbg-red-200" }) : null
        })));
    }

    getCanDeactivateMessage(): string {
        return this.isChanged() ? "Geänderte Noten verwerfen?" : null;
    }

    isChanged(): boolean {
        this.bufferedIsChanged = this.getChanges().anyChanges();
        return this.bufferedIsChanged;
    }

    getChanges(): GradeBookSchoolClassUpsertDto {
        let result = new GradeBookSchoolClassUpsertDto();
        result.id = this.schoolClass.id;

        let old = this.oldSchoolClass;
        let nw = this.schoolClass;

        let oldExams = old?.exams ?? [];
        let newExams = nw?.exams ?? [];

        let oldExamMap = new Map(oldExams.map(e => [e.id, e]));
        let newExamMap = new Map(newExams.map(e => [e.id, e]));

        result.addedExams = newExams.filter(e => e.id <= 0 || !oldExamMap.has(e.id));

        result.removedExamIds = oldExams.filter(e => !newExamMap.has(e.id)).map(e => e.id);

        result.changedExams = newExams.filter(e => {
            let oldExam = oldExamMap.get(e.id);
            if (!oldExam) {
                return false;
            }

            return e.title !== oldExam.title ||
                e.domain !== oldExam.domain ||
                e.examType?.id !== oldExam.examType?.id ||
                !Dates.eq(e.date, oldExam.date);
        });

        let oldGrades = oldExams.flatMap(e => e.grades ?? []);
        let newGrades = newExams.flatMap(e => e.grades ?? []);

        let oldGradeMap = new Map(oldGrades.map(g => [g.id, g]));
        let newGradeMap = new Map(newGrades.map(g => [g.id, g]));

        result.removedGradeIds = oldGrades.filter(g => !newGradeMap.has(g.id)).map(g => g.id);
        result.addedGrades = [];
        result.changedGrades = [];
        for (let exam of oldExams) {
            let newExam = newExamMap.get(exam.id);
            if (newExam == null) {
                continue;
            }

            result.addedGrades = [...result.addedGrades, ...newExam.grades.filter(g => g.id <= 0 || !oldGradeMap.has(g.id)).map(g => new ExamGradeUpsertDto(g, exam.id))];

            let changedGrades = newExam.grades.filter(g => {
                let oldGrade = oldGradeMap.get(g.id);
                if (!oldGrade) {
                    return false;
                }

                return g.pupilId !== oldGrade.pupilId ||
                    g.title !== oldGrade.title ||
                    !Dates.eq(g.date, oldGrade.date) ||
                    g.grade !== oldGrade.grade ||
                    g.exam?.id !== oldGrade.exam?.id;
            });
            if (changedGrades.length > 0) {
                result.changedGrades = [...result.changedGrades, ...changedGrades.map(g => new ExamGradeUpsertDto(g, exam.id))];
            }
        }

        result.changedCertificateGrades = [];
        if (this.usesGradeCertificates) {
            for (let key of Objects.getPropertyNames(this.certificateGradeMap)) {
                let pupilId = Utils.toNumber(Strings.getSubstringTo(key, "|"));
                let domain = Strings.getSubstringFrom(key, "|");
                let cg = this.certificateGrades.find(item => item.pupilId == pupilId && item.domain == domain);
                let grade = this.certificateGradeMap[key];
                if (cg == null && grade != "") {
                    cg = new CertificateGrade(pupilId, domain, grade);
                    result.changedCertificateGrades.push(cg);
                }
                else if (cg != null && cg.grade != grade) {
                    cg = Objects.cloneDeep(cg);
                    cg.grade = grade;
                    result.changedCertificateGrades.push(cg);
                }
            }
        }

        return result;
    }

    async editExam(event: any, exam: Exam, isNew = false): Promise<void> {
        this.editedExam = exam;
        let editExamForm = new FrontendForm([
            new FrontendField("examType", "Typ", FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.settings.examTypes.filter(et => !et.isInvalidForGrade(this.enaioSchoolClass.grade)), item => [item.id, item.title])
            }),
            new FrontendField("title", "Thema", FrontendFieldType.string, { mandatory: true}),
            new FrontendField("date", "Datum", FrontendFieldType.date, { mandatory: true })
        ]);
        editExamForm.fill(exam);
        //TODO Übersetzung von Unterobjekt in ID notwendig für Forms. Irgendwann irgendwie eleganter lösen bitte
        editExamForm.setValue("examType", exam.examType != null ? exam.examType.id : 0);
        switch (await this.app.dialog.form(editExamForm, isNew ? "Leistungsnachweis hinzufügen" : "Leistungsnachweis bearbeiten",
            isNew ? [this.app.dialog.ok, this.app.dialog.cancel] : [this.app.dialog.ok, this.app.dialog.cancel, this.app.dialog.delete])) {
            case "ok":
                editExamForm.get(this.editedExam);
                this.editedExam.examType = Objects.cloneDeep(this.settings.examTypes.find(et => et.id ==
                            (this.editedExam.examType as any as number))
                );

                //Jedes Exam benötigt für das Bearbeiten eine eindeutige ID. Vor dem Speichern werden die Random-IDs < 0 auf 0 gesetzt, damit das Backend sie in die DB einfügt
                if (isNew) {
                    this.editedExam.id = -1;
                    while (this.schoolClass.exams.some(exam2 => exam2.id == this.editedExam.id)) {
                        this.editedExam.id--;
                    }
                }

                this.schoolClass.exams = isNew ? [ ...this.schoolClass.exams, exam] : [...this.schoolClass.exams];
                this.examsUpdated();
                break;
            case "delete":
                if (await this.app.dialog.yesNo("Möchten Sie den ausgewählten Leistungsnachweis wirklich löschen? Alle vergebenen Noten werden nach dem Speichern unwiderruflich gelöscht!", "Warnung")) {
                    let index = this.schoolClass.exams.findIndex(e => e == this.editedExam);
                    this.schoolClass.exams.splice(index, 1);
                    this.examsUpdated();
                }
                break;
        }
    }

    async save(): Promise<void> {
        let changed = this.getChanges();        
        if (!changed.anyChanges()) {
            await this.app.dialog.info("Es wurden keine Änderungen vorgenommen");
            return;
        }
        this.app.showSpinner = true;

        await this.app.saveDataHandler2(new SaveDataHandler({
            worker: async () => RestEndpoint.main().body({schoolClass: changed}).put().run("api/cer/gradebookschoolclass/v2").list(GradeHistory),
            componentView: this        
        }));

        await this.initParams();
        this.app.showSpinner = false;
    }

    export(): void {
        window.open(RestEndpoint.main().query({schoolClassId: this.schoolClassId, isHalfYear: this.isHalfYear}).action("api/cer/gradebookschoolclass/export").generateUrl());
    }

    async addExam(event: any): Promise<void> {
        await this.editExam(event, this.newExam(this.selectedDomain), true);
    }

    newExam(domain: string): Exam {
        let result = Objects.fromPlain(Exam, { domain, date: new Date(), examType: Objects.cloneDeep(this.settings.examTypes[0])});
        result.fillMissingGrades(this.pupils);
        return result;
    }

    showExamTypes(event: any): void {
        let ds = this.settings.domainSettings.find(ds2 => ds2.domain == this.selectedDomain);
        this.examTypeFactorsTable = new TableData([
                new TableColumn("examType", "Art des Leistungsnachweises", TableCellType.string, 20), 
                new TableColumn("factor", "Gewichtung", TableCellType.string, 5)
            ],
            this.settings.examTypes.map(et => new TableRow("", et, {
                examType: et.title,
                factor: Utils.formatNumber(ds.getExamTypeFactorNormalised(et.id, this.settings.examTypes), 2) + "%"
            })));
        this.opExamTypeFactors.show(event);
    }

    formatValue(value: number, overrideRoundingType: NumberFormatMode = null): string {
        return value == null || value == -1 ? "" : this.settings.formatValue(value, overrideRoundingType);
    }

    formatValueBoth(value: number, value2: number, overrideRoundingType: NumberFormatMode = null): string {
        let tg = this.gradeSettings.isUseTextualGrades() ? value2 : null;
        return this.formatValue(value, overrideRoundingType) + (tg != null ? " (" + Utils.formatNumber(tg, 2) + ")" : "");
    }

    formatTextualGrade(value: number, textualGrade?: boolean, forDictation?: boolean): string {
        return value == null || value == -1 ? "" : this.gradeSettings.getTextualGrade(value, textualGrade, forDictation);
    }

    gradeUpdated(): void {
        this.averageMap = this.schoolClass.getPupilAverageMap(this.globalSettings, this.settings, this.enaioSchoolClass, this.selectedSemester, this.pupils);
        this.averageMapTextualGrade = this.schoolClass.getPupilAverageMapTextualGrade(this.globalSettings, this.settings, this.enaioSchoolClass.grade, this.enaioSchoolClass, this.selectedSemester, this.pupils);
        this.schoolClass.exams.forEach(exam => exam.calculateAverage());
        if (this.pupilsTable != null) {
            for (let row of this.pupilsTable.rows) {
                let pupil = row.raw as Pupil;
                if ( this.usesGradeCertificates && this.pupilsWithCertificates.includes(pupil.id)) {
                    if (this.certificateGradeMap[pupil.id + '|' + this.selectedDomain] != row.values.certificateGrade) {
                        this.certificateGradeMap[pupil.id + '|' + this.selectedDomain] = row.values.certificateGrade;
                    }
                }
            }
        }
        this.updatePupilsTable();
    }

    selectAll(event: any): void {
        event.target.select();
    }

    private examsUpdated(): void {
        this.examsByDomain = Arrays.toMultiMapKeys(this.schoolClass.exams, e => e.domain);
        for (let domain of Object.keys(this.examsByDomain)) {
            this.examsByDomain[domain] = Arrays.sortBy(this.examsByDomain[domain], e => e.date, false);
        }
        this.gradeUpdated();
    }

}
